import { Injectable } from "@angular/core";
import { Observable, of, from } from "rxjs";
import { map, catchError, concatMap, filter, take, defaultIfEmpty, tap, timeoutWith } from "rxjs/operators";
import { UsersService } from "./users.service";
import { WebhooksService } from "./webhooks.service";
import { CurpCallbackKey, CurpData, CurpProvider, CurpCallbacks } from "../interfaces/curp.interface";
import { CURP } from "../utilities/curp";

@Injectable({ providedIn: "root" })
export class CurpOrchestratorService {
  private providers: Record<CurpCallbackKey, CurpProvider>;
  private cache = new Map<string, CurpData>();
  private readonly default_timeout: number = 3 * 1000; // 3 seconds

  /** Default order (cheap → expensive) */
  private order: CurpCallbackKey[] = [
    CurpCallbacks.CACHE,
    CurpCallbacks.API_USERS,
    CurpCallbacks.SCRAPING,
    CurpCallbacks.NUBARIUM,
  ];

  constructor(
    private _usersService: UsersService,
    private _webhookService: WebhooksService // scrapping + nubarium
  ) {
    this.providers = {
      // In-memory cache provider
      [CurpCallbacks.CACHE]: (curp) => of(this.cache.get(curp) ?? null),

      // /api/users/by_curp
      [CurpCallbacks.API_USERS]: (curp) =>
        this._usersService.getUserByCurp(curp).pipe(
          map((res: { user: any } | null) => {
            const user = res?.user ?? null;
            if (!user) return null;

            return {
              first_name: user.first_name ?? "",
              middle_name: user.middle_name ?? "",
              first_last_name: user.first_last_name ?? "",
              second_last_name: user.second_last_name ?? "",
              email: user.email ?? "",
              _source: "API_USERS",
              _user: user, // raw payload for UI decisions (duplicate CURP, etc.)
            };
          }),
          catchError(() => of(null))
        ),

      // Nubarium external provider
      [CurpCallbacks.NUBARIUM]: (curp) =>
        this._webhookService.nubariumCurpInfo(curp).pipe(
          timeoutWith(this.default_timeout, of(null)),
          map((res: any) => {
            if (!res || res.status !== "OK" || !res.name) return null;

            const name_parts = res.name.trim().split(/\s+/);
            const first_name = name_parts.shift() ?? "";
            const middle_name = name_parts.join(" ");

            return {
              first_name,
              middle_name,
              first_last_name: res.first_last_name ?? "",
              second_last_name: res.second_last_name ?? "",
              sex: res.sex,
              birthdate: res.birthdate,
              rfc: res.rfc ?? "",
              _source: "NUBARIUM",
            };
          }),
          catchError(() => of(null))
        ),

      // Scraping external provider
      [CurpCallbacks.SCRAPING]: (curp) =>
        this._webhookService.scrappingCurpInfo(curp).pipe(
          timeoutWith(this.default_timeout, of(null)),
          map((res: any | null) => {
            if (!res || "error" in res) return null;

            const parts = (res.name ?? "").trim().split(/\s+/);
            const first_name = parts.shift() ?? "";
            const middle_name = parts.join(" ");

            return {
              first_name,
              middle_name,
              first_last_name: res.first_last_name ?? "",
              second_last_name: res.second_last_name ?? "",
              sex: res.sex,
              birthdate: res.birthdate,
              rfc: res.rfc ?? "",
              _source: "SCRAPING",
            };
          }),
          catchError(() => of(null))
        ),
    };
  }

  /** Change provider order at runtime */
  configure(order: CurpCallbackKey[]): CurpOrchestratorService {
    this.order = order;
    return this;
  }

  /** CURP format + checksum validation (uppercase, trimmed) */
  validateRegexCurp(curp: string): boolean {
    if (!curp || curp.length !== 18) return false;
    const up = curp.toUpperCase().trim();
    return CURP.validate(up) && CURP.validateLastDigit(up);
  }

  /**
   * Sequentially executes providers in configured order.
   * Returns the FIRST non-null result; otherwise null.
   * Caches successful results in-memory keyed by normalized CURP.
   */
  getCurpDetails(curp: string): Observable<CurpData | null> {
    const CURP_KEY = (curp || "").toUpperCase().trim();
    const chain = this.order.map((key) => this.providers[key]);

    return from(chain).pipe(
      concatMap((fn) => fn(CURP_KEY)), // run providers sequentially
      map((res) => res || null),
      tap((res) => {
        if (res) {
          this.cache.set(CURP_KEY, { ...res, _cached: true });
        }
      }),
      filter((res) => !!res), // pick first truthy
      take(1),
      defaultIfEmpty(null)
    );
  }

  /** Execute a specific provider only (still caches on success) */
  getFrom(key: CurpCallbackKey, curp: string): Observable<CurpData | null> {
    const CURP_KEY = (curp || "").toUpperCase().trim();
    return this.providers[key](CURP_KEY).pipe(
      map((res) => res || null),
      tap((res) => {
        if (res) {
          this.cache.set(CURP_KEY, { ...res, _cached: true });
        }
      })
    );
  }
}
